import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, Observable, Subject, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SETTINGS } from '../config/common.settings';
import { EncryptionService } from './encryption';
import { Router } from '@angular/router';

export interface LoginResponse {
  message: string;
  requiresOTP: boolean;
  userId?: string;
  user?: {
    _id: string;
    firstName: string;
    lastName: string;
    userName: string;
    role: any;
    email: string;
    isFirstLogin?: boolean;
    accountStatus?: string;
    requiresAccountSetup?: boolean;
    accountSetupStep?: number;
    status: "INVITED" | "PENDING" | "APPROVED" | "DENIED" | "DEACTIVATED" | "LOCKED";
    subscriberId?: string;
  };
}

export interface LoginOTPVerificationResponse {
  message: string;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    userName: string;
    role: any;
    email: string;
    isFirstLogin?: boolean;
    accountStatus?: string;
    requiresAccountSetup?: boolean;
    accountSetupStep?: number;
    status: "INVITED" | "PENDING" | "APPROVED" | "DENIED" | "DEACTIVATED" | "LOCKED";
    subscriberId?: string;
    language?: string;
  };
  accessToken?: string; // Added for access token
  subscriptionStatus?: string; // Chargebee subscription status
  subscriptionMessage?: string; // Message about subscription status
  requiresPayment?: boolean; // Whether user needs to pay (SA users)
}

export interface EmailOTPResponse {
  done: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private loginStatus = new Subject();
  private tokenRefreshInterval?: any;
  private visibilityChangeHandler?: () => void;
  
  constructor(private http: HttpClient, private encryption: EncryptionService, private router: Router) {
    this.initializeTokenRefresh();
  }

  ngOnDestroy(): void {
    this.stopTokenRefresh();
  }

  postLogin(payload: { username: string; password: string; recaptchaToken?: string }): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/users/login`, payload);
  }

  verifyLoginOTP(payload: { userId: string; otp: string }): Observable<LoginOTPVerificationResponse> {
    return this.http.post<LoginOTPVerificationResponse>(`${environment.apiUrl}/users/verify-login-otp`, payload).pipe(
      tap(response => this.setSession(response))
    );
  }

  sendEmailVerificationOTP(email: string): Observable<EmailOTPResponse> {
    return this.http.post<EmailOTPResponse>(`${environment.apiUrl}/users/send-email-verification-otp`, { email });
  }

  verifyEmailOTP(email: string, otp: string): Observable<EmailOTPResponse> {
    return this.http.post<EmailOTPResponse>(`${environment.apiUrl}/users/verify-email-otp`, { email, otp });
  }

  private setSession(authResult: LoginOTPVerificationResponse): void {
    this.setLoggedInUser(JSON.stringify(authResult.user));
    
    // Store access token if provided
    if (authResult.accessToken) {
      localStorage.setItem(SETTINGS.ACCESS_TOKEN, this.encryption.encrypt(authResult.accessToken));
    }
    
    this.loginStatus.next({ isAuthenticated: true });
    
    // Start automatic token refresh after login
    this.initializeTokenRefresh();
  }

  setDirectLoginSession(user: any, accessToken?: string): void {
    this.setLoggedInUser(JSON.stringify(user));
    
    // Store access token if provided
    if (accessToken) {
      localStorage.setItem(SETTINGS.ACCESS_TOKEN, this.encryption.encrypt(accessToken));
    }
    
    this.loginStatus.next({ isAuthenticated: true });
    
    // Start automatic token refresh after login
    this.initializeTokenRefresh();
  }

  setLoggedInUser(userStr: string): void {
    localStorage.setItem(
      SETTINGS.LOGGED_IN_USER,
      this.encryption.encrypt(userStr),
    );
  }

  getLoginStatus(): Observable<any> {
    return this.loginStatus.asObservable();
  }

  logout(): void {
    console.log('AuthService: Logging out user...');
    this.stopTokenRefresh();
    localStorage.clear();
    this.loginStatus.next({ isAuthenticated: false });
    window.location.href = '/';
  }

  // Check if logout was intentional or due to an error
  isLogoutIntentional(): boolean {
    // If we have a user but no access token, it might be a temporary issue
    const user = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    const accessToken = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
    
    if (user && !accessToken) {
      console.log('AuthService: Potential temporary logout - user exists but no access token');
      return false;
    }
    
    return true;
  }

  isAuthenticated(): boolean {
    const user = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    const accessToken = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
    
    // Check if both user and access token exist
    if (!user || !accessToken) {
      console.log('AuthService: Missing user or access token');
      return false;
    }
    
    try {
      // Verify the access token can be decrypted
      const decryptedToken = this.encryption.decrypt(accessToken);
      if (!decryptedToken) {
        console.log('AuthService: Failed to decrypt access token');
        return false;
      }
      
      // Basic JWT validation (check if it's not empty and has proper format)
      if (typeof decryptedToken !== 'string' || decryptedToken.split('.').length !== 3) {
        console.log('AuthService: Invalid JWT token format');
        return false;
      }
      
      // Check if token is expired
      if (this.isTokenExpired(decryptedToken)) {
        console.log('AuthService: Access token has expired');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('AuthService: Error validating access token:', error);
      return false;
    }
  }

  /**
   * Decode JWT token and check if it's expired
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) {
        return false; // If no expiration, assume valid
      }
      // Check if token expires within the next minute (buffer for network delays)
      const expirationTime = exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const bufferTime = 60 * 1000; // 1 minute buffer
      return currentTime >= (expirationTime - bufferTime);
    } catch (error) {
      console.error('AuthService: Error checking token expiration:', error);
      return false; // If we can't parse, assume valid to avoid false positives
    }
  }

  /**
   * Initialize automatic token refresh mechanism
   */
  private initializeTokenRefresh(): void {
    // Check if user data exists (even if access token is expired)
    const user = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    const accessToken = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
    
    // If no user data at all, don't initialize refresh
    if (!user) {
      return;
    }

    // If access token exists but is expired, try to refresh it immediately
    if (accessToken) {
      try {
        const decryptedToken = this.encryption.decrypt(accessToken);
        if (this.isTokenExpired(decryptedToken)) {
          console.log('AuthService: Access token expired, attempting automatic refresh on initialization');
          this.proactiveTokenRefresh().subscribe({
            next: () => {
              console.log('AuthService: Token refreshed successfully on initialization');
              // After successful refresh, start periodic refresh
              this.startPeriodicTokenRefresh();
              this.setupVisibilityChangeHandler();
            },
            error: (error) => {
              console.error('AuthService: Failed to refresh expired token on initialization:', error);
              // If refresh fails with 401, refresh token is expired - clear everything
              if (error.status === 401) {
                console.log('AuthService: Refresh token expired, clearing session');
                this.stopTokenRefresh();
                localStorage.clear();
              }
              // Don't start periodic refresh if initial refresh failed
            }
          });
          return; // Exit early, periodic refresh will start after successful refresh
        }
      } catch (error) {
        console.error('AuthService: Error checking token on initialization:', error);
        // If we can't decrypt, don't start refresh
        return;
      }
    }

    // If token is valid or doesn't exist but user data exists, start periodic refresh
    // (Token refresh will handle getting a new token if needed)
    this.startPeriodicTokenRefresh();

    // Refresh token when app regains focus
    this.setupVisibilityChangeHandler();
  }

  /**
   * Start periodic token refresh
   */
  private startPeriodicTokenRefresh(): void {
    // Clear any existing interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    // Refresh token every 10 minutes (before 15min expiration)
    const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

    this.tokenRefreshInterval = setInterval(() => {
      // Check if user data exists (even if token is expired)
      const user = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      if (!user) {
        // No user data, stop refreshing
        this.stopTokenRefresh();
        return;
      }

      console.log('AuthService: Periodic token refresh triggered');
      this.proactiveTokenRefresh().subscribe({
        next: () => {
          console.log('AuthService: Periodic token refresh successful');
        },
        error: (error) => {
          console.error('AuthService: Periodic token refresh failed:', error);
          // If refresh fails with 401, refresh token expired - stop refreshing and clear session
          if (error.status === 401) {
            console.log('AuthService: Refresh token expired, stopping periodic refresh');
            this.stopTokenRefresh();
            localStorage.clear();
          }
        }
      });
    }, REFRESH_INTERVAL);

    // Also refresh immediately if token is expired or about to expire
    const accessToken = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
    if (accessToken) {
      try {
        const decryptedToken = this.encryption.decrypt(accessToken);
        if (this.isTokenExpired(decryptedToken)) {
          console.log('AuthService: Token expired on app load, refreshing immediately');
          this.proactiveTokenRefresh().subscribe({
            next: () => {
              console.log('AuthService: Initial token refresh successful');
            },
            error: (error) => {
              console.error('AuthService: Initial token refresh failed:', error);
            }
          });
        }
      } catch (error) {
        console.error('AuthService: Error checking token on initialization:', error);
      }
    }
  }

  /**
   * Setup handler to refresh token when app regains focus
   */
  private setupVisibilityChangeHandler(): void {
    if (typeof document === 'undefined') {
      return; // Server-side rendering
    }

    // Remove existing handler if any
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        // Check if user data exists (even if token is expired)
        const user = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
        if (!user) {
          return; // No user data, don't try to refresh
        }

        // Check if token is expired or about to expire
        const accessToken = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
        if (accessToken) {
          try {
            const decryptedToken = this.encryption.decrypt(accessToken);
            if (this.isTokenExpired(decryptedToken)) {
              console.log('AuthService: Token expired when app regained focus, refreshing...');
              this.proactiveTokenRefresh().subscribe({
                next: () => {
                  console.log('AuthService: Token refreshed after app regained focus');
                },
                error: (error) => {
                  console.error('AuthService: Token refresh failed after app regained focus:', error);
                  // If refresh fails with 401, refresh token expired - clear session
                  if (error.status === 401) {
                    console.log('AuthService: Refresh token expired, clearing session');
                    this.stopTokenRefresh();
                    localStorage.clear();
                  }
                }
              });
            }
          } catch (error) {
            console.error('AuthService: Error checking token on visibility change:', error);
          }
        } else {
          // No access token but user data exists - try to refresh
          console.log('AuthService: No access token when app regained focus, attempting refresh...');
          this.proactiveTokenRefresh().subscribe({
            next: () => {
              console.log('AuthService: Token obtained after app regained focus');
            },
            error: (error) => {
              console.error('AuthService: Failed to obtain token after app regained focus:', error);
              if (error.status === 401) {
                this.stopTokenRefresh();
                localStorage.clear();
              }
            }
          });
        }
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Stop automatic token refresh
   */
  private stopTokenRefresh(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = undefined;
    }

    if (this.visibilityChangeHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }
  }

  requestResetPassword(payload: any) {
    return this.http.post(`${environment.apiUrl}/users/request-reset-password`, payload);
  }

  validatePWResetToken(token: any) {
    return this.http.post(`${environment.apiUrl}/users/validate-reset-password`, { token });
  }

  resetPassword(payload: any) {
    return this.http.post(`${environment.apiUrl}/users/reset-password`, payload);
  }

  refreshToken(): Observable<any> {
    console.log('AuthService: Attempting to refresh token...');
    return this.http
      .post(`${environment.apiUrl}/users/refresh`, {}, { withCredentials: true })
      .pipe(
        tap((res: any) => {
          console.log('AuthService: Token refresh response:', res);
          if (res?.accessToken) {
            localStorage.setItem(
              SETTINGS.ACCESS_TOKEN,
              this.encryption.encrypt(res.accessToken),
            );
            console.log('AuthService: New access token stored successfully');
            
            // Restart periodic refresh if it was stopped
            if (!this.tokenRefreshInterval && this.isAuthenticated()) {
              this.startPeriodicTokenRefresh();
            }
          } else {
            console.warn('AuthService: Token refresh response missing accessToken:', res);
          }
        }),
        catchError((err) => {
          console.error('AuthService: Token refresh failed:', err);
          // Only logout if it's a 401 (authentication failure)
          // Don't logout on 403 as that could be a permission error, not auth failure
          if (err.status === 401) {
            console.log('AuthService: Authentication failed, logging out...');
            this.stopTokenRefresh();
            localStorage.clear();
            this.router.navigate(['/']);
          } else {
            console.log('AuthService: Non-authentication error, not logging out:', err.status);
          }
          return throwError(() => err);
        }),
      );
  }

  getRefreshToken(): any { return null; }

  // Proactively refresh token before it expires
  proactiveTokenRefresh(): Observable<any> {
    console.log('AuthService: Proactively refreshing token...');
    return this.refreshToken().pipe(
      tap(() => {
        console.log('AuthService: Proactive token refresh successful');
      }),
      catchError((error) => {
        console.error('AuthService: Proactive token refresh failed:', error);
        // Don't logout on proactive refresh failure, just log the error
        return throwError(() => error);
      })
    );
  }

  getCurrentUser(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/me`);
  }

  markFirstLoginComplete(): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/me/first-login-complete`, {});
  }

  shouldRedirectToAccountSetup(user: any): boolean {
    return user?.isFirstLogin && user?.accountStatus === 'PENDING';
  }

  getLoggedInUser(): any {
    const userStrEnc: any = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    return JSON.parse(this.encryption.decrypt(userStrEnc));
  }

  /**
   * Check if user has a valid session and route them to appropriate page
   * Returns true if user was routed, false if no valid session
   */
  checkSessionAndRoute(router: Router, userService?: any): Observable<boolean> {
    return new Observable(observer => {
      // Check if user data exists
      const user = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      const accessToken = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
      
      if (!user || !accessToken) {
        observer.next(false);
        observer.complete();
        return;
      }

      try {
        // Get user data
        const userData = this.getLoggedInUser();
        
        // Check if token is expired - if so, try to refresh first
        const decryptedToken = this.encryption.decrypt(accessToken);
        if (this.isTokenExpired(decryptedToken)) {
          // Token expired, try to refresh
          this.refreshToken().subscribe({
            next: () => {
              // Token refreshed, now route
              this.routeUserBasedOnStatus(router, userData, userService, observer);
            },
            error: () => {
              // Refresh failed, no valid session
              observer.next(false);
              observer.complete();
            }
          });
        } else {
          // Token is valid, route immediately
          this.routeUserBasedOnStatus(router, userData, userService, observer);
        }
      } catch (error) {
        console.error('AuthService: Error checking session:', error);
        observer.next(false);
        observer.complete();
      }
    });
  }

  /**
   * Route user based on their account status, setup step, and subscription
   */
  private routeUserBasedOnStatus(router: Router, user: any, userService: any, observer: any): void {
    const accountSetupStep = user.accountSetupStep || null;
    const userRole = user?.role?.identification;
    const isSA = userRole === 'SA';
    const isApproved = user?.status === 'APPROVED';
    
    console.log('Routing user based on status:', {
      accountSetupStep,
      userRole,
      termsAccepted: user?.termsAccepted,
      status: user?.status,
      isApproved
    });
    
    // Helper function to get appropriate dashboard route based on user role
    const getDashboardRoute = () => {
      return this.getDashboardRouteByRole(userRole);
    };
    
    // For SA users, follow the 4-step setup process
    if (isSA) {
      // If user is approved and has accepted terms, treat as setup complete
      if (isApproved && user?.termsAccepted) {
        console.log('SA user is APPROVED and has accepted terms - checking subscription directly');
        if (userService) {
          this.checkSubscriptionAndRoute(router, userService, userRole, getDashboardRoute(), observer);
        } else {
          router.navigate([getDashboardRoute()]);
          observer.next(true);
          observer.complete();
        }
        return;
      }
      
      // Handle each setup step for non-approved or incomplete users
      switch (accountSetupStep) {
        case 1:
          router.navigate(['/user-account-setup'], { queryParams: { step: '1' } });
          observer.next(true);
          observer.complete();
          break;
        case 2:
          router.navigate(['/user-account-setup'], { queryParams: { step: '2' } });
          observer.next(true);
          observer.complete();
          break;
        case 3:
          if (!user?.termsAccepted) {
            router.navigate(['/user-account-setup']);
            observer.next(true);
            observer.complete();
          } else if (!isApproved) {
            router.navigate(['/account-pending-approval']);
            observer.next(true);
            observer.complete();
          } else {
            if (userService) {
              this.checkSubscriptionAndRoute(router, userService, userRole, getDashboardRoute(), observer);
            } else {
              router.navigate([getDashboardRoute()]);
              observer.next(true);
              observer.complete();
            }
          }
          break;
        case null:
        default:
          if (isApproved) {
            if (userService) {
              this.checkSubscriptionAndRoute(router, userService, userRole, getDashboardRoute(), observer);
            } else {
              router.navigate([getDashboardRoute()]);
              observer.next(true);
              observer.complete();
            }
          } else {
            router.navigate(['/account-pending-approval']);
            observer.next(true);
            observer.complete();
          }
          break;
      }
    } else {
      // Non-SA Users (A, FSR, CSM, SM, etc.)
      switch (accountSetupStep) {
        case 1:
          router.navigate(['/user-account-setup'], { queryParams: { step: '1' } });
          observer.next(true);
          observer.complete();
          break;
        case 2:
          router.navigate(['/user-account-setup'], { queryParams: { step: '2' } });
          observer.next(true);
          observer.complete();
          break;
        case 3:
        case null:
        default:
          if (isApproved && (userRole === 'A' || userRole === 'FSR')) {
            if (userService) {
              this.checkSubscriptionAndRoute(router, userService, userRole, getDashboardRoute(), observer);
            } else {
              router.navigate([getDashboardRoute()]);
              observer.next(true);
              observer.complete();
            }
          } else if (isApproved) {
            router.navigate([getDashboardRoute()]);
            observer.next(true);
            observer.complete();
          } else {
            router.navigate(['/account-pending-approval']);
            observer.next(true);
            observer.complete();
          }
          break;
      }
    }
  }

  /**
   * Check subscription status and route accordingly
   */
  private checkSubscriptionAndRoute(router: Router, userService: any, userRole: string, dashboardRoute: string, observer: any): void {
    if (!userService || !userService.checkSubscriptionStatus) {
      router.navigate([dashboardRoute]);
      observer.next(true);
      observer.complete();
      return;
    }

    userService.checkSubscriptionStatus().subscribe({
      next: (subscriptionStatus: any) => {
        if (!subscriptionStatus.isValid) {
          if (subscriptionStatus.requiresPayment) {
            router.navigate(['/payment/sub']);
          } else {
            router.navigate(['/subscription-inactive'], {
              state: {
                message: subscriptionStatus.message,
                userRole: userRole
              }
            });
          }
        } else {
          router.navigate([dashboardRoute]);
        }
        observer.next(true);
        observer.complete();
      },
      error: (error: any) => {
        console.error('Error checking subscription:', error);
        router.navigate([dashboardRoute]);
        observer.next(true);
        observer.complete();
      }
    });
  }

  /**
   * Get the appropriate dashboard route based on user role
   * Note: Language prefix handling should be done at the component level
   * since this service doesn't have access to LanguageService
   */
  private getDashboardRouteByRole(roleIdentification?: string): string {
    switch (roleIdentification) {
      case 'FSR':
        return '/admin/fsr-dashboard';
      case 'SA':
      case 'A':
        return '/admin/admin-dashboard';
      case 'CSM':
      case 'SM':
        return '/admin/user-management';
      default:
        return '/admin/user-management';
    }
  }
}
